(() => {
  const STORAGE_KEY = 'ignitionFinanceContext';
  const referralSalesperson = params => {
    const values = params.getAll('salesperson');
    const value = values.length === 1 ? values[0].trim().toLowerCase() : '';
    return ['byron', 'dave'].includes(value) ? value : '';
  };
  const newFinanceReference = () => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  };
  const referralSource = (salesperson, reference) =>
    `https://ignitionmotorsports.ca/pages/finance-application?salesperson=${salesperson}&finance_ref=${reference}`;
  const applyReferralSource = (form, context, fieldId) => {
    if (!context.salesperson || !/^[a-f0-9]{32}$/.test(context.financeReference || '')) return;
    context.sourcePageUrl = referralSource(context.salesperson, context.financeReference);
    const field = form.querySelector(`#${fieldId}`);
    if (field) field.value = context.sourcePageUrl;
  };
  const LEGACY_STORAGE_KEY = 'ignition_finance_context_v1';
  const CONTEXT_TTL_MS = 4 * 60 * 60 * 1000;
  const cleanValue = value => /^(?:n\/?a(?:\.ca)?|-none-|null|undefined)$/i.test(String(value ?? '').trim()) ? '' : String(value ?? '').trim();
  const hasValue = value => cleanValue(value) !== '';
  const clearStoredContext = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (e) {}
  };

  const parseMoney = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const formatMoney = (value) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency: 'CAD',
        maximumFractionDigits: 2
      }).format(parseMoney(value));
    } catch (e) {
      return `$${parseMoney(value).toFixed(2)}`;
    }
  };

  const readStoredContext = () => {
    try {
      const current = sessionStorage.getItem(STORAGE_KEY);
      const legacy = sessionStorage.getItem(LEGACY_STORAGE_KEY);
      const context = JSON.parse(current || legacy || 'null');
      if (!context) return null;
      const storedAt = Date.parse(context.capturedAt || context.startedAt || context.leadSubmittedAt || '');
      if (!Number.isFinite(storedAt) || storedAt > Date.now() + 300000 || Date.now() - storedAt > CONTEXT_TTL_MS) {
        clearStoredContext();
        return null;
      }
      return context;
    } catch (e) {
      return null;
    }
  };

  const saveContext = (context) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    } catch (e) {}
  };

  const contextFromUrl = () => {
    let url = new URL(window.location.href);
    const identityKeys = ['vehicle', 'vehicle_title', 'model', 'stock', 'stock_number', 'product_id', 'shopify_product_id', 'crm_key'];
    const hasIdentity = candidate => identityKeys.some(key => hasValue(candidate.searchParams.get(key)));
    if (!hasIdentity(url) && !referralSalesperson(url.searchParams)) {
      try {
        const referrer = new URL(document.referrer);
        if (referrer.origin !== url.origin || !/^\/pages\/finance-now\/?$/.test(referrer.pathname) || !hasIdentity(referrer)) return null;
        url = referrer;
      } catch (e) { return null; }
    }
    const params = url.searchParams;
    const first = (...keys) => keys.map(key => cleanValue(params.get(key))).find(Boolean) || '';
    const vehicle = first('vehicle', 'vehicle_title', 'model');

    return {
      salesperson: referralSalesperson(params),
      financeReference: /^[a-f0-9]{32}$/.test(params.get('finance_ref') || '') ? params.get('finance_ref') : '',
      source: params.get('source') || 'vehicle_page',
      vehicle,
      variant: params.get('variant') || '',
      stock: first('stock', 'stock_number'),
      productId: first('product_id', 'shopify_product_id'),
      variantId: first('variant_id', 'shopify_variant_id'),
      productUrl: first('product_url', 'shopify_product_url'),
      vendor: params.get('vendor') || '',
      productType: params.get('product_type') || '',
      pageType: params.get('page_type') || '',
      crmKey: params.get('crm_key') || '',
      financeProgram: params.get('finance_program') || '',
      financeSource: params.get('finance_source') || '',
      apr: first('apr', 'finance_apr'),
      term: first('term', 'term_months', 'finance_term'),
      frequency: first('frequency', 'payment_frequency'),
      payment: first('payment', 'estimated_payment'),
      downPayment: params.get('down_payment') || '',
      tradeEquity: params.get('trade_equity') || '',
      amountFinanced: params.get('amount_financed') || '',
      taxRate: params.get('tax_rate') || '',
      taxIncluded: params.get('tax_included') === 'true',
      sourcePageUrl: first('source_page_url', 'source_url') || url.href,
      capturedAt: new Date().toISOString()
    };
  };

  const sameVehicle = (a, b) => {
    if (!a || !b) return false;
    const keys = ['stock', 'productId', 'variantId', 'crmKey'];
    const norm = value => cleanValue(value).toUpperCase().replace(/^GID:\/\/SHOPIFY\/[^/]+\//, '').replace(/[ _-]/g, '');
    if (keys.some(key => hasValue(a[key]) && hasValue(b[key]) && norm(a[key]) !== norm(b[key]))) return false;
    if (keys.some(key => hasValue(a[key]) && hasValue(b[key]))) return true;
    if (keys.some(key => hasValue(a[key]) || hasValue(b[key]))) return false;
    return hasValue(a.vehicle) && norm(a.vehicle) === norm(b.vehicle);
  };

  const financeSourceFor = context => {
    if (['Direct Application', 'Get This Payment'].includes(context.financeSource)) return context.financeSource;
    return context.source === 'get_this_payment' || [context.apr, context.term, context.payment].some(hasValue) ? 'Get This Payment' : 'Direct Application';
  };

  const clearMetadataPlaceholders = form => {
    form.querySelectorAll('#LEADCF13, #LEADCF9, #LEADCF11, #LEADCF10, #LEADCF12, #COBJ6CF33, #COBJ6CF34, #COBJ6CF32, #COBJ6CF31, #COBJ6CF5').forEach(el => {
      if (el.value && !hasValue(el.value) && !el.options) el.value = '';
    });
  };

  const buildVehicleLabel = (context) => {
    let label = context.vehicle || '';
    if (context.variant) label += ` - ${context.variant}`;
    if (context.stock && !label.toUpperCase().includes(String(context.stock).toUpperCase())) label += ` | Stock #${context.stock}`;
    return label;
  };

  const makeSummary = (context, heading = 'Financing this vehicle') => {
    const card = document.createElement('div');
    card.className = 'ignition-finance-context';
    card.setAttribute('data-finance-context-summary', '');

    const meta = [];
    if (context.stock) meta.push(`Stock #${context.stock}`);
    if (context.apr && context.term) meta.push(`${context.apr}% APR · ${context.term} months · OAC`);
    if (context.downPayment && parseMoney(context.downPayment) > 0) meta.push(`${formatMoney(context.downPayment)} down`);
    if (context.tradeEquity && parseMoney(context.tradeEquity) > 0) meta.push(`${formatMoney(context.tradeEquity)} trade equity`);

    const payment = context.payment && context.frequency
      ? `<div class="ignition-finance-context__payment">Estimated payment: <strong>${formatMoney(context.payment)}</strong> ${escapeHtml(context.frequency)}</div>`
      : '';

    card.innerHTML = `
      <span class="ignition-finance-context__eyebrow">${heading}</span>
      <strong class="ignition-finance-context__vehicle"></strong>
      ${meta.length ? `<div class="ignition-finance-context__meta">${meta.map(item => `<span>${escapeHtml(item)}</span>`).join('')}</div>` : ''}
      ${payment}
      <div class="ignition-finance-context__note">Payment is an estimate. Final lender approval, licensing and contract terms may change the payment.</div>
    `;
    card.querySelector('.ignition-finance-context__vehicle').textContent = context.vehicle || 'Selected vehicle';
    return card;
  };

  const setIfBlank = (id, value) => {
    if (!hasValue(value)) return;
    const el = document.getElementById(id);
    if (el && !hasValue(el.value)) el.value = value;
  };

  const selectOption = (id, value) => {
    if (!value) return;
    const el = document.getElementById(id);
    if (!el) return;
    if (!el.options) { el.value = value; return; }
    const requested = String(value).trim().toLowerCase();
    Array.from(el.options || []).forEach(option => {
      if (String(option.value || '').trim().toLowerCase() === requested) option.selected = true;
    });
  };

  const normalizeFrequency = (value) => {
    const normalized = String(value || '').toLowerCase().replace(/[^a-z]/g, '');
    if (normalized === 'weekly' || normalized === 'week') return 'Weekly';
    if (['biweekly', 'biweek', 'everytwoweeks', 'fortnightly'].includes(normalized)) return 'Biweekly';
    if (normalized === 'monthly' || normalized === 'month') return 'Monthly';
    return '';
  };

  const vehicleInterestFor = (context) => {
    const vendor = (context.vendor || '').toLowerCase();
    const type = (context.productType || '').toLowerCase();
    if (vendor.includes('aprilia')) return 'New Aprilia';
    if (vendor.includes('moto guzzi')) return 'New Moto Guzzi';
    if (vendor.includes('cfmoto')) {
      if (type.includes('motorcycle') || type.includes('bike')) return 'New CFMOTO Motorcycle';
      return 'New CFMOTO Offroad';
    }
    if (vendor.includes('talaria')) return 'New Talaria';
    if (vendor.includes('fantic')) return 'New Fantic';
    return '';
  };

  const addPrivacyNotice = (form, text) => {
    if (form.querySelector('[data-finance-privacy-notice]')) return;
    const notice = document.createElement('p');
    notice.className = 'ignition-finance-context__note';
    notice.setAttribute('data-finance-privacy-notice', '');
    notice.append(document.createTextNode(`${text} `));
    const link = document.createElement('a');
    link.href = '/policies/privacy-policy';
    link.textContent = 'Privacy policy';
    notice.append(link, document.createTextNode('.'));
    form.prepend(notice);
  };

  const initQuickStart = (form) => {
    clearMetadataPlaceholders(form);
    addPrivacyNotice(form, 'We use the details submitted here to respond to your financing request and continue the application you choose to complete.');
    const urlContext = contextFromUrl();
    let context = urlContext || { source: 'direct_application', financeSource: 'Direct Application', sourcePageUrl: window.location.href, capturedAt: new Date().toISOString() };
    clearStoredContext();
    context.salesperson = referralSalesperson(new URL(window.location.href).searchParams);
    context.financeReference = context.salesperson ? newFinanceReference() : '';
    applyReferralSource(form, context, 'LEADCF12');
    saveContext(context);

    const vehicleField = document.getElementById('LEADCF2');
    if (vehicleField && context.vehicle) {
      vehicleField.value = buildVehicleLabel(context).slice(0, 255);
    }

    setIfBlank('LEADCF13', context.stock);
    setIfBlank('LEADCF71', context.apr);
    setIfBlank('LEADCF51', context.term);
    selectOption('LEADCF14', normalizeFrequency(context.frequency));
    setIfBlank('LEADCF67', context.payment);
    setIfBlank('LEADCF69', context.downPayment);
    setIfBlank('LEADCF68', context.tradeEquity);
    setIfBlank('LEADCF70', context.amountFinanced);
    setIfBlank('LEADCF9', context.productId);
    setIfBlank('LEADCF11', context.variantId);
    setIfBlank('LEADCF10', context.productUrl);
    setIfBlank('LEADCF12', context.sourcePageUrl || document.referrer);
    selectOption(
      'LEADCF15',
      financeSourceFor(context)
    );

    const isPaymentHandoff = context.source === 'get_this_payment' && context.payment;
    if (isPaymentHandoff) {
      const existing = document.querySelector('[data-finance-context-summary]');
      if (!existing) form.prepend(makeSummary(context));
      if (vehicleField) {
        const row = vehicleField.closest('.zcwf_row');
        if (row) row.style.display = 'none';
      }
    }

    // Shopify's theme editor renders the storefront inside an iframe. Zoho redirects the
    // Web-to-Lead form back to our full credit application after submission; if that redirect
    // stays inside the editor iframe, the storefront's frame policy blocks it. Promote only
    // embedded submissions to the top-level browsing context. On the live storefront this
    // remains a normal same-window form submission.
    if (window.top !== window.self) {
      form.setAttribute('target', '_top');
    }

    form.addEventListener('submit', event => {
      if (event.defaultPrevented) return;
      applyReferralSource(form, context, 'LEADCF12');
      context = {
        ...context,
        vehicle: cleanValue(vehicleField?.value) || context.vehicle || '',
        stock: cleanValue(document.getElementById('LEADCF13')?.value),
        productId: cleanValue(document.getElementById('LEADCF9')?.value),
        variantId: cleanValue(document.getElementById('LEADCF11')?.value),
        productUrl: cleanValue(document.getElementById('LEADCF10')?.value),
        firstName: document.getElementById('First_Name')?.value || '',
        lastName: document.getElementById('Last_Name')?.value || '',
        email: document.getElementById('Email')?.value || '',
        phone: document.getElementById('Phone')?.value || '',
        tradeInDetails: document.getElementById('Description')?.value || '',
        financeSource: document.getElementById('LEADCF15')?.value || context.financeSource || '',
        sourcePageUrl: document.getElementById('LEADCF12')?.value || context.sourcePageUrl || '',
        contact: {
          firstName: document.getElementById('First_Name')?.value || '',
          lastName: document.getElementById('Last_Name')?.value || '',
          email: document.getElementById('Email')?.value || '',
          phone: document.getElementById('Phone')?.value || ''
        },
        leadSubmittedAt: new Date().toISOString(),
        startedAt: new Date().toISOString()
      };
      saveContext(context);

      // Carry the non-sensitive finance/vehicle context through Zoho's return redirect as a
      // fallback. This keeps the selected unit/payment intact even when browser storage is
      // partitioned by an embedded preview. Contact details stay out of the URL.
      const returnField = form.querySelector('input[name="returnURL"]');
      if (returnField && (context.vehicle || context.salesperson)) {
        try {
          const returnUrl = new URL('/pages/full-credit-application', window.location.origin);
          const params = {
            salesperson: context.salesperson,
            finance_ref: context.financeReference,
            source: context.source || 'get_this_payment',
            vehicle: context.vehicle,
            variant: context.variant,
            stock: context.stock,
            product_id: context.productId,
            variant_id: context.variantId,
            product_url: context.productUrl,
            vendor: context.vendor,
            product_type: context.productType,
            page_type: context.pageType,
            crm_key: context.crmKey,
            finance_program: context.financeProgram,
            finance_source: context.financeSource,
            apr: context.apr,
            term: context.term,
            frequency: context.frequency,
            payment: context.payment,
            down_payment: context.downPayment,
            trade_equity: context.tradeEquity,
            amount_financed: context.amountFinanced,
            tax_rate: context.taxRate,
            tax_included: context.taxIncluded ? 'true' : ''
          };
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && String(value) !== '') {
              returnUrl.searchParams.set(key, String(value));
            }
          });
          try {
            const sourceUrl = new URL(context.sourcePageUrl, window.location.origin);
            if (context.salesperson) {
              returnUrl.searchParams.set('source_page_url', context.sourcePageUrl);
            } else if (sourceUrl.origin === window.location.origin) {
              returnUrl.searchParams.set('source_page_url', sourceUrl.origin + sourceUrl.pathname);
            }
          } catch (e) {}

          // Preserve an unpublished-theme preview through Zoho's redirect so Step 2 loads
          // with the same theme/assets while we are testing v12.
          const currentParams = new URLSearchParams(window.location.search);
          ['preview_theme_id', '_fd', 'pb'].forEach(key => {
            const value = currentParams.get(key);
            if (value) returnUrl.searchParams.set(key, value);
          });

          returnField.value = returnUrl.toString();
        } catch (e) {}
      }
    });
  };

  const initFullApplication = (form) => {
    clearMetadataPlaceholders(form);
    addPrivacyNotice(form, 'This form collects personal and financial details for your financing application. Submit only after reviewing the authorization and privacy information.');
    const returnField = form.querySelector('input[name="returnURL"]');
    if (returnField) {
      returnField.value = new URL('/pages/finance-now?application=received', window.location.origin).toString();
    }
    const urlContext = contextFromUrl();
    const stored = readStoredContext();
    const sameReferral = Boolean(urlContext?.salesperson && stored?.leadSubmittedAt &&
      urlContext.salesperson === stored.salesperson && urlContext.financeReference &&
      urlContext.financeReference === stored.financeReference);
    const samePublicVehicle = !urlContext?.salesperson && !stored?.salesperson && sameVehicle(urlContext, stored);
    const context = urlContext ? (sameReferral || samePublicVehicle ? { ...stored, ...urlContext } : urlContext) : (stored?.leadSubmittedAt ? stored : null);
    form.addEventListener('submit', event => { if (!event.defaultPrevented) clearStoredContext(); });
    if (!context) return;
    applyReferralSource(form, context, 'COBJ6CF5');
    saveContext(context);

    const contact = context.contact || {
      firstName: context.firstName,
      lastName: context.lastName,
      email: context.email,
      phone: context.phone
    };
    setIfBlank('COBJ6CF3', contact.firstName);
    setIfBlank('COBJ6CF2', contact.lastName);
    setIfBlank('COBJ6CF4', contact.phone);
    setIfBlank('Email', contact.email);

    setIfBlank('COBJ6CF8', buildVehicleLabel(context));
    if (context.payment && context.frequency) {
      setIfBlank('COBJ6CF19', `${formatMoney(context.payment)} ${context.frequency} (estimated)`);
    }
    if (context.productType) setIfBlank('COBJ6CF21', context.productType);
    selectOption('COBJ6CF17', vehicleInterestFor(context));

    setIfBlank('COBJ6CF20', context.tradeInDetails);
    setIfBlank('COBJ6CF33', context.stock);
    setIfBlank('COBJ6CF69', context.apr);
    setIfBlank('COBJ6CF60', context.term);
    setIfBlank('COBJ6CF36', normalizeFrequency(context.frequency));
    setIfBlank('COBJ6CF71', context.payment);
    setIfBlank('COBJ6CF70', context.downPayment);
    setIfBlank('COBJ6CF73', context.tradeEquity);
    setIfBlank('COBJ6CF72', context.amountFinanced);
    setIfBlank('COBJ6CF34', context.productId);
    setIfBlank('COBJ6CF32', context.variantId);
    setIfBlank('COBJ6CF31', context.productUrl);
    selectOption('COBJ6CF35', financeSourceFor(context));
    setIfBlank('COBJ6CF5', context.sourcePageUrl);

    const titles = Array.from(form.querySelectorAll('.finance-section-title'));
    const vehicleTitle = titles.find(el => /Vehicle\s*&\s*Financing/i.test(el.textContent || ''));
    if (vehicleTitle && context.vehicle && !document.querySelector('[data-finance-context-summary]')) {
      vehicleTitle.before(makeSummary(context, 'Your selected vehicle & payment'));
    }
  };

  const init = () => {
    const quickStart = document.getElementById('webform6505746000004970068');
    const params = new URLSearchParams(window.location.search);
    if (params.get('application') === 'received') {
      const notice = document.createElement('div');
      notice.className = 'alert bg-success-bg text-success-text';
      notice.setAttribute('role', 'status');
      notice.innerHTML = '<strong>Application received.</strong> Thank you. Our finance team will review it and contact you if anything else is needed.';
      const host = quickStart?.closest('#crmWebToEntityForm') || quickStart;
      if (host) {
        host.before(notice);
        host.hidden = true;
      }
      clearStoredContext();
      return;
    }
    if (quickStart) initQuickStart(quickStart);

    const fullApplication = document.getElementById('webform6505746000005064001');
    if (fullApplication) initFullApplication(fullApplication);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

